/**
 * Addressing of game components inside the level document, so editors can read and write component
 * fields without caring where the data lives:
 * - prefab components of placed objects are written as prefab overrides (`mods`)
 * - components of prefab-less objects are written into their data
 * - behaviour components of bodies (FadeOutHandler, RigidBodyRotator, ...) can exist in several copies
 *   (body components, tile layer extra components, body prefab overrides); every copy is kept in sync
 */
import type { Draft } from 'immer';
import type { Body, CustomComponent, LevelDoc, PlacedObject, PropMod } from '../model/format.ts';
import { applyPropertyPath, arr } from '../model/unityProps.ts';
import { instantiatePrefab, type InstNode } from '../data/prefabInstance.ts';
import { gameData } from '../data/gameData.ts';
import { draftObject, findObject, uid } from './store.ts';
import { objectInfo } from './scene.ts';
import { syncGenerators } from './resize.ts';
import { emptyPlayerOverrides } from './schema.ts';

export type ModValue = { v?: string; r?: string } | null;

export type CompAddr =
  | { kind: 'objMod'; obj: string; t: string }
  | { kind: 'objCustom'; obj: string; index: number }
  | { kind: 'bodyComp'; body: string; type: string }
  | { kind: 'moving'; body: string };

export interface CompRef {
  addr: CompAddr;
  type: string;
  data: any;
  /** child GameObject name when the component is not on the prefab root */
  child?: string;
  node?: InstNode;
}

export function ownerRef(addr: CompAddr): string {
  return addr.kind === 'objMod' || addr.kind === 'objCustom' ? `obj:${addr.obj}` : `body:${addr.body}`;
}

/** The PlayerOverrides play mode will apply: first active enabled component, level objects then body objects. */
export function playerOverridesComp(doc: LevelDoc): CompRef | null {
  const objs = [...doc.objects, ...doc.bodies.flatMap((b) => b.objects)];
  for (const o of objs) {
    if (o.active === false) continue;
    for (const c of objectComps(doc, o)) {
      if (c.type === 'PlayerOverrides' && String(c.data?.m_Enabled ?? '1') !== '0') return c;
    }
  }
  return null;
}

/** Add a level object whose fields all start unused, so play keeps the player prefab until one is checked. */
export function addPlayerOverrides(d: Draft<LevelDoc>): string {
  const id = uid('o');
  d.objects.push({ id, name: 'PlayerOverrides', prefab: null, x: 0, y: 0, rotation: 0, scale: [1, 1], mods: [], components: [{ type: 'PlayerOverrides', data: emptyPlayerOverrides() }] });
  return id;
}

/** Components of a placed object (prefab instance with overrides applied, or custom components). */
export function objectComps(doc: LevelDoc, o: PlacedObject): CompRef[] {
  if (!o.prefab) return (o.components ?? []).map((c, index) => ({ addr: { kind: 'objCustom', obj: o.id, index }, type: c.type, data: c.data }));
  const inst = objectInfo(doc, o).inst;
  if (!inst) return [];
  return inst.gos.flatMap((g) =>
    g.comps.map((c) => ({ addr: { kind: 'objMod', obj: o.id, t: c.id } as CompAddr, type: c.type, data: c.data, child: g === inst.root ? undefined : g.name, node: c })),
  );
}

const bodyPrefabCache = new WeakMap<Body, ReturnType<typeof instantiatePrefab>>();
function bodyPrefab(b: Body) {
  if (!b.prefab) return null;
  let inst = bodyPrefabCache.get(b);
  if (inst === undefined) {
    inst = instantiatePrefab(b.prefab, b.mods);
    bodyPrefabCache.set(b, inst);
  }
  return inst;
}

/** Copies of a component type on a body: body components, tile layer extra components, owned by the body prefab. */
function bodyCopies(b: Body | Draft<Body>, type: string): CustomComponent[] {
  const out: CustomComponent[] = [];
  for (const c of b.components ?? []) if (c.type === type) out.push(c as CustomComponent);
  for (const l of b.layers) for (const c of l.extraComponents ?? []) if (c.type === type) out.push(c as CustomComponent);
  return out;
}

/** Behaviour component types present on a body (excluding Rigidbody2D and generator flags). */
export function bodyCompTypes(b: Body): string[] {
  const out: string[] = [];
  const add = (t: string) => {
    if (!out.includes(t) && !/^(Rigidbody2D|SpikeTilemapGeneratorFlag|OneWayColliderGeneratorFlag|Tilemap|TilemapRenderer|TilemapCollider2D|CompositeCollider2D|Transform|GameObject|Grid|NetworkObject|ColorPropertyBlockController|SpikeHandler)$/.test(t)) out.push(t);
  };
  for (const c of b.components ?? []) add(c.type);
  for (const l of b.layers) for (const c of l.extraComponents ?? []) add(c.type);
  if (b.behavior !== 'movingPlatform') {
    const inst = bodyPrefab(b);
    if (inst) for (const g of inst.gos) for (const c of g.comps) if (/Handler$|Rotator$|Modifier$/.test(c.type)) add(c.type);
  }
  return out;
}

export function bodyCompData(b: Body, type: string): any | null {
  const copies = bodyCopies(b, type);
  if (copies.length) return copies[0].data;
  const inst = bodyPrefab(b);
  if (inst) for (const g of inst.gos) for (const c of g.comps) if (c.type === type) return c.data;
  return null;
}

export function bodyComps(b: Body): CompRef[] {
  return bodyCompTypes(b).map((type) => ({ addr: { kind: 'bodyComp', body: b.id, type }, type, data: bodyCompData(b, type) }));
}

/** Current data of an addressed component (read-only). */
export function compData(doc: LevelDoc, addr: CompAddr): any | null {
  switch (addr.kind) {
    case 'objMod': {
      const f = findObject(doc, addr.obj);
      return f ? objectInfo(doc, f.obj).inst?.nodes.get(addr.t)?.data ?? null : null;
    }
    case 'objCustom': {
      const f = findObject(doc, addr.obj);
      return f?.obj.components?.[addr.index]?.data ?? null;
    }
    case 'bodyComp': {
      const b = doc.bodies.find((x) => x.id === addr.body);
      return b ? bodyCompData(b, addr.type) : null;
    }
    case 'moving': {
      const b = doc.bodies.find((x) => x.id === addr.body);
      return b?.moving ? { activators: b.moving.activators.map((r) => ({ ref: r })) } : null;
    }
  }
}

export function setModIn(mods: Draft<PropMod>[], t: string, p: string, value: ModValue) {
  const i = mods.findIndex((m) => m.t === t && m.p === p);
  if (!value) {
    if (i >= 0) mods.splice(i, 1);
    return;
  }
  const m: PropMod = value.r !== undefined ? { t, p, r: value.r } : { t, p, v: value.v ?? '' };
  if (i >= 0) mods[i] = m;
  else mods.push(m);
}

export function setCustomField(data: any, path: string, v: ModValue) {
  if (!v) return;
  if (v.r !== undefined) applyPropertyPath(data, path, '', v.r ? { fileID: '1', ref: v.r } : { fileID: '0' });
  else applyPropertyPath(data, path, v.v ?? '', undefined);
}

function prefabNodeId(b: Draft<Body>, type: string): string | null {
  if (!b.prefab) return null;
  const def = gameData().prefabs[b.prefab];
  if (!def) return null;
  for (const [id, n] of Object.entries(def.nodes)) if (n.type === type) return id;
  return null;
}

/** Set one serialized field (Unity property path) of an addressed component. */
export function writeField(d: Draft<LevelDoc>, addr: CompAddr, path: string, value: ModValue) {
  switch (addr.kind) {
    case 'objMod': {
      const o = draftObject(d, addr.obj);
      if (o) setModIn(o.mods, addr.t, path, value);
      syncGenerators(d, addr.obj);
      return;
    }
    case 'objCustom': {
      const c = draftObject(d, addr.obj)?.components?.[addr.index];
      if (c) setCustomField(c.data, path, value);
      syncGenerators(d, addr.obj);
      return;
    }
    case 'bodyComp': {
      const b = d.bodies.find((x) => x.id === addr.body);
      if (!b) return;
      for (const c of bodyCopies(b, addr.type)) setCustomField(c.data, path, value);
      const nid = prefabNodeId(b, addr.type);
      if (nid) setModIn(b.mods, nid, path, value);
      return;
    }
    case 'moving':
      return;
  }
}

/** Replace a whole list field. Items are flat records of field -> string value or {ref}. */
export function writeList(d: Draft<LevelDoc>, addr: CompAddr, path: string, items: (string | Record<string, string | { ref: string }> | { ref: string })[]) {
  if (addr.kind === 'moving') {
    const b = d.bodies.find((x) => x.id === addr.body);
    if (b?.moving) b.moving.activators = items.map((i) => (typeof i === 'object' && 'ref' in i ? (i.ref as string) : '')).filter(Boolean);
    return;
  }
  const asData = (i: (typeof items)[number]): any => {
    if (typeof i === 'string') return i;
    if ('ref' in i && typeof i.ref === 'string') return { fileID: '1', ref: i.ref };
    const o: any = {};
    for (const [k, v] of Object.entries(i)) {
      const keys = k.split('.');
      let cur = o;
      for (const kk of keys.slice(0, -1)) cur = cur[kk] ??= {};
      cur[keys[keys.length - 1]] = typeof v === 'object' ? { fileID: '1', ref: v.ref } : v;
    }
    return o;
  };
  const writeMods = (mods: Draft<PropMod>[], t: string) => {
    for (let i = mods.length - 1; i >= 0; i--) if (mods[i].t === t && (mods[i].p === path || mods[i].p.startsWith(`${path}.Array`))) mods.splice(i, 1);
    mods.push({ t, p: `${path}.Array.size`, v: String(items.length) });
    items.forEach((it, n) => {
      const base = `${path}.Array.data[${n}]`;
      if (typeof it === 'string') mods.push({ t, p: base, v: it });
      else if ('ref' in it && typeof it.ref === 'string') mods.push({ t, p: base, r: it.ref });
      else for (const [k, v] of Object.entries(it)) mods.push(typeof v === 'object' ? { t, p: `${base}.${k}`, r: v.ref } : { t, p: `${base}.${k}`, v });
    });
  };
  const setData = (data: any) => {
    const head = path.split('.');
    let cur = data;
    for (const k of head.slice(0, -1)) cur = cur[k] ??= {};
    cur[head[head.length - 1]] = items.map(asData);
  };
  switch (addr.kind) {
    case 'objMod': {
      const o = draftObject(d, addr.obj);
      if (o) writeMods(o.mods, addr.t);
      return;
    }
    case 'objCustom': {
      const c = draftObject(d, addr.obj)?.components?.[addr.index];
      if (c) setData(c.data);
      return;
    }
    case 'bodyComp': {
      const b = d.bodies.find((x) => x.id === addr.body);
      if (!b) return;
      for (const c of bodyCopies(b, addr.type)) setData(c.data);
      const nid = prefabNodeId(b, addr.type);
      if (nid) writeMods(b.mods, nid);
      return;
    }
  }
}

/** Entity ref held by a serialized reference value. */
export function refOf(v: any): string | null {
  if (typeof v === 'string') return v || null;
  if (!v || typeof v !== 'object') return null;
  const r = v.entity ?? v.ref;
  return typeof r === 'string' && r ? r : null;
}

export function refsOf(v: any): string[] {
  return arr(v).map(refOf).filter((r): r is string => !!r);
}

/** Read a (possibly nested) field of component data. */
export function fieldAt(data: any, path: string): any {
  let cur = data;
  for (const k of path.split('.')) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[k];
  }
  return cur;
}
